import Image from '@tiptap/extension-image'
import { mergeAttributes } from '@tiptap/core'

const uploadAttribute = (name) => ({
  default: null,
  parseHTML: (element) => element.getAttribute(name),
  renderHTML: (attributes) => attributes[name] === null ? {} : { [name]: attributes[name] },
})

const setOptionalAttribute = (element, name, value) => {
  if (value === null || value === undefined || value === '') {
    element.removeAttribute(name)
  } else {
    element.setAttribute(name, String(value))
  }
}

/**
 * Image keeps its compact, ordinary <img> serialization. The custom NodeView
 * only adds a local wrapper/status while a temporary upload is processing, so
 * progress UI never becomes part of the persisted rich-text dialect.
 */
export default Image.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      uploadMessages: {},
    }
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      uploadId: {
        ...uploadAttribute('data-rich-text-upload-id'),
        renderHTML: ({ uploadId }) => uploadId === null
          ? {}
          : { 'data-rich-text-upload-id': uploadId },
      },
      uploadState: {
        ...uploadAttribute('data-rich-text-upload-state'),
        renderHTML: ({ uploadState }) => uploadState === null
          ? {}
          : { 'data-rich-text-upload-state': uploadState },
      },
      uploadProgress: {
        default: null,
        parseHTML: (element) => {
          const value = Number.parseInt(element.getAttribute('data-rich-text-upload-progress'), 10)

          return Number.isFinite(value) ? value : null
        },
        renderHTML: ({ uploadProgress }) => uploadProgress === null
          ? {}
          : { 'data-rich-text-upload-progress': uploadProgress },
      },
      uploadToken: {
        ...uploadAttribute('data-rich-text-upload-token'),
        renderHTML: ({ uploadToken }) => uploadToken === null
          ? {}
          : { 'data-rich-text-upload-token': uploadToken },
      },
      mediaReference: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-rich-text-media'),
        renderHTML: ({ mediaReference }) => mediaReference === null
          ? {}
          : { 'data-rich-text-media': mediaReference },
      },
    }
  },

  parseHTML() {
    return [
      ...(this.parent?.() || []),
      { tag: 'img[data-rich-text-media]' },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const attributes = { ...HTMLAttributes }

    // A transient editor preview may carry both values. The UUID remains the
    // persisted source of truth; concrete URLs never leave getHTML().
    if (attributes['data-rich-text-media']) {
      delete attributes.src
    }

    return ['img', mergeAttributes(this.options.HTMLAttributes, attributes)]
  },

  addNodeView() {
    const messages = this.options.uploadMessages || {}

    return ({ node }) => {
      const wrapper = document.createElement('span')
      const image = document.createElement('img')
      const status = document.createElement('span')

      wrapper.className = 'rich-text-inline-image'
      wrapper.contentEditable = 'false'
      image.draggable = true
      status.className = 'rich-text-inline-image-status'
      status.setAttribute('aria-live', 'polite')
      wrapper.append(image, status)

      const render = (currentNode) => {
        const {
          src,
          alt,
          title,
          width,
          height,
          mediaReference,
          uploadState,
          uploadProgress,
        } = currentNode.attrs

        const unresolvedReference = Boolean(mediaReference) && !src

        setOptionalAttribute(image, 'src', src)
        setOptionalAttribute(image, 'alt', alt)
        setOptionalAttribute(image, 'title', title)
        setOptionalAttribute(image, 'width', width)
        setOptionalAttribute(image, 'height', height)
        setOptionalAttribute(image, 'data-rich-text-media', mediaReference)
        setOptionalAttribute(wrapper, 'data-rich-text-upload-state', uploadState)
        setOptionalAttribute(wrapper, 'data-rich-text-upload-progress', uploadProgress)
        setOptionalAttribute(
          wrapper,
          'data-rich-text-media-unresolved',
          unresolvedReference ? 'true' : null,
        )
        image.hidden = unresolvedReference

        if (uploadState === null) {
          status.hidden = !unresolvedReference
          status.textContent = unresolvedReference
            ? messages.imageUnavailable || 'Stored image unavailable'
            : ''

          return
        }

        status.hidden = false

        if (uploadState === 'error') {
          status.textContent = messages.imageUploadFailed || 'Image upload failed'
        } else if (uploadState === 'uploading') {
          const label = messages.imageUploading || 'Uploading image…'
          status.textContent = Number.isFinite(uploadProgress)
            ? `${label} ${uploadProgress}%`
            : label
        } else {
          status.textContent = messages.imageProcessing || 'Processing image…'
        }
      }

      render(node)

      return {
        dom: wrapper,
        update: (updatedNode) => {
          if (updatedNode.type !== node.type) {
            return false
          }

          render(updatedNode)

          return true
        },
      }
    }
  },
})
